import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import * as ObjectTypeIds from 'customer-data-objects/constants/ObjectTypeIds';
import { SELF, makeDynamicFilterFamily, parseDynamicFilterFamilyId, testDynamicFilterFamily } from '../filterQueryFormat/DynamicFilterFamilies';
import { useCallback, useContext, useMemo } from 'react';
import { hasActivityFilters, hasActivityObjectDisabled } from '../permissions/gates';
import { useEditorActions } from './useEditorActions';
import { useStrategy } from './useStrategy';
import ActivityFilterInfoTooltip from '../components/common/ActivityFilterInfoTooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
export const ACTIVITY_OBJECTS = [ObjectTypeIds.CALL_TYPE_ID, ObjectTypeIds.COMMUNICATION_TYPE_ID, ObjectTypeIds.CONVERSATION_SESSION_TYPE_ID, ObjectTypeIds.EMAIL_TYPE_ID, ObjectTypeIds.MEETING_EVENT_TYPE_ID, ObjectTypeIds.NOTE_TYPE_ID, ObjectTypeIds.POSTAL_MAIL_TYPE_ID, ObjectTypeIds.PUBLISHING_TASK_TYPE_ID, ObjectTypeIds.TASK_TYPE_ID];
const ItemComponent = _ref => {
  let {
      children,
      option
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if ('options' in option || option.value !== ObjectTypeIds.ENGAGEMENT_TYPE_ID && option.value !== ObjectTypeIds.EMAIL_TYPE_ID) {
    return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
      children: children
    }));
  }
  if (option.value === ObjectTypeIds.EMAIL_TYPE_ID) {
    return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
      children: /*#__PURE__*/_jsx(Tooltip, {
        disabled: !option.disabled,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.emailFilterInfo.description"
        }),
        children: children
      })
    }));
  }
  return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
    children: /*#__PURE__*/_jsx(ActivityFilterInfoTooltip, {
      disabled: !option.disabled,
      children: children
    })
  }));
};
const getObjectTypeSelectOptions = (baseFilterFamily, getEnabledAssociatedObjectTypeIds, getFilterFamilyObjectName, excludeDropdownText) => {
  const baseObjectName = getFilterFamilyObjectName(baseFilterFamily);
  const options = [{
    text: I18n.text('customerDataFiltersUI.objectTypeSelect.currentObject'),
    options: [excludeDropdownText ? {
      text: baseObjectName,
      value: makeDynamicFilterFamily(SELF, baseFilterFamily)
    } : {
      dropdownText: baseObjectName,
      text: I18n.text('customerDataFiltersUI.objectTypeSelect.options.currentObject', {
        singularObjectType: baseObjectName
      }),
      value: makeDynamicFilterFamily(SELF, baseFilterFamily)
    }]
  }];
  const associatedObjectTypeIds = getEnabledAssociatedObjectTypeIds();
  if (associatedObjectTypeIds.length) {
    const associatedObjectOptions = associatedObjectTypeIds.map(objectTypeId => {
      const objectName = getFilterFamilyObjectName(objectTypeId);
      const sharedProps = {
        value: objectTypeId
      };
      if (hasActivityFilters() && hasActivityObjectDisabled() && baseFilterFamily !== ObjectTypeIds.ENGAGEMENT_TYPE_ID && objectTypeId === ObjectTypeIds.ENGAGEMENT_TYPE_ID) {
        sharedProps.disabled = true;
      }
      if (!hasActivityObjectDisabled() && baseFilterFamily !== ObjectTypeIds.EMAIL_TYPE_ID && objectTypeId === ObjectTypeIds.EMAIL_TYPE_ID) {
        sharedProps.disabled = true;
      }
      if (hasActivityFilters() && ACTIVITY_OBJECTS.includes(objectTypeId)) {
        sharedProps.badge = /*#__PURE__*/_jsx(Badge, {
          use: "new",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "newCustomerDataFilters.filterGroupEditor.newBadge"
          })
        });
      }
      if (excludeDropdownText) {
        return Object.assign({}, sharedProps, {
          text: objectName
        });
      }
      return Object.assign({}, sharedProps, {
        dropdownText: objectName,
        text: I18n.text('customerDataFiltersUI.objectTypeSelect.options.associatedObject', {
          singularObjectType: objectName
        })
      });
    }).sort(({
      text: textA
    }, {
      text: textB
    }) => textA.localeCompare(textB));
    options.push({
      text: I18n.text('customerDataFiltersUI.objectTypeSelect.associatedObject'),
      options: associatedObjectOptions
    });
  }
  return options;
};
export const useObjectTypeSelect = excludeDropdownText => {
  const {
    conditionPathToChangeFieldFor,
    conditionToRefineKeyPath,
    draftCondition,
    selectedObjectType,
    props: {
      baseFilterFamily,
      uiMessages: {
        filteringObjectDropdownLabel
      }
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getEnabledAssociatedObjectTypeIds,
    getFilterFamilyObjectName
  } = useStrategy();
  const {
    onObjectTypeSelect
  } = useEditorActions();
  const options = useMemo(() => getObjectTypeSelectOptions(baseFilterFamily, getEnabledAssociatedObjectTypeIds, getFilterFamilyObjectName, excludeDropdownText), [baseFilterFamily, getEnabledAssociatedObjectTypeIds, getFilterFamilyObjectName, excludeDropdownText]);
  const value = useMemo(() => {
    const {
      filterFamily,
      isObjectOwnFilterBranch
    } = selectedObjectType || {
      filterFamily: baseFilterFamily,
      isObjectOwnFilterBranch: true
    };
    return isObjectOwnFilterBranch ? makeDynamicFilterFamily(SELF, filterFamily) : filterFamily;
  }, [baseFilterFamily, selectedObjectType]);
  const onChange = useCallback(evt => {
    const filterFamily = evt.target.value;
    onObjectTypeSelect({
      filterFamily: parseDynamicFilterFamilyId(SELF, filterFamily),
      isObjectOwnFilterBranch: testDynamicFilterFamily(SELF, filterFamily)
    });
  }, [onObjectTypeSelect]);
  const isFilterFamilyPredefined = Boolean(draftCondition && draftCondition.isAddingNew && draftCondition.filterFamily);
  const hasOnlyOneOption = options.length === 1 || isFilterFamilyPredefined || !!conditionPathToChangeFieldFor || !!conditionToRefineKeyPath;
  return {
    defaultValue: makeDynamicFilterFamily(SELF, baseFilterFamily),
    filteringObjectDropdownLabel,
    hasOnlyOneOption,
    isCurrentObject: !selectedObjectType || selectedObjectType.isObjectOwnFilterBranch,
    itemComponent: ItemComponent,
    options,
    value,
    onChange
  };
};